import { router } from "@inertiajs/react"
import { useState } from "react"

import {
  CUSTOM_INSTRUCTIONS_PLACEHOLDERS,
  CustomInstructionsField,
} from "@/components/agents/setup/custom-instructions"
import { OptionCard } from "@/components/agents/setup/review-responder/option-card"
import type {
  CriticismHandling,
  PraiseHandling,
  ResponseTone,
} from "@/components/agents/setup/review-responder/types"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Switch } from "@/components/ui/switch"
import {
  Tabs,
  TabsBar,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { setupAgentPath } from "@/routes"

// The slice of setup_data the Edit drawer owns — the agent's response config,
// mirroring the setup wizard so edits map onto the same keys the agent was
// created with (kept in sync with Agents::ReviewResponder::SetupValidator).
// starting_point is a one-time go-live choice, and the connected profile lives
// in Integrations (disconnecting there breaks the responder) — both omitted, as
// is the account-wide knowledge base, which is managed on its own surface.
// IMP-439: the whole Approval surface is hidden for launch — the agent always
// drafts for approval (Auto-post removed), so approval_mode and its auto-post-
// only settings are dropped from the editable slice; the server defaults/locks
// it to "draft" regardless.
interface EditableFields {
  response_tone: ResponseTone
  sign_off_name: string
  match_language: boolean
  disclose_ai: boolean
  criticism_handling: CriticismHandling
  praise_handling: PraiseHandling
  additional_instructions: string
}

const TONE_OPTIONS: {
  value: ResponseTone
  label: string
  description: string
}[] = [
  {
    value: "professional",
    label: "Professional",
    description:
      "Polished and precise. Best for healthcare, finance, or legal.",
  },
  {
    value: "warm",
    label: "Warm",
    description: "Friendly and genuine. Works for most local businesses.",
  },
  {
    value: "conversational",
    label: "Conversational",
    description:
      "Relaxed and natural. Great for hospitality or consumer brands.",
  },
]

const CRITICISM_OPTIONS: {
  value: CriticismHandling
  label: string
  description: string
}[] = [
  {
    value: "make-it-right",
    label: "Offer to make it right",
    description:
      "Acknowledge the issue and invite the reviewer to get in touch.",
  },
  {
    value: "acknowledge",
    label: "Acknowledge without committing",
    description: "Validate and express regret, without promising a resolution.",
  },
]

const PRAISE_OPTIONS: {
  value: PraiseHandling
  label: string
  description: string
}[] = [
  {
    value: "specific",
    label: "Specific and personal",
    description: "Pick up on details in the review and weave them in.",
  },
  {
    value: "short",
    label: "Short and gracious",
    description: "A brief, genuine thank-you. Reads well when volume is high.",
  },
]

function readFields(data: Record<string, unknown>): EditableFields {
  const str = (k: string): string =>
    typeof data[k] === "string" ? data[k] : ""
  const bool = (k: string, fallback: boolean): boolean =>
    typeof data[k] === "boolean" ? data[k] : fallback
  const oneOf = <T extends string>(k: string, allowed: T[], fallback: T): T =>
    allowed.includes(data[k] as T) ? (data[k] as T) : fallback
  return {
    response_tone: oneOf(
      "response_tone",
      TONE_OPTIONS.map((o) => o.value),
      "warm",
    ),
    sign_off_name: str("sign_off_name"),
    match_language: bool("match_language", true),
    disclose_ai: bool("disclose_ai", false),
    criticism_handling: oneOf(
      "criticism_handling",
      CRITICISM_OPTIONS.map((o) => o.value),
      "make-it-right",
    ),
    praise_handling: oneOf(
      "praise_handling",
      PRAISE_OPTIONS.map((o) => o.value),
      "specific",
    ),
    additional_instructions: str("additional_instructions"),
  }
}

export function ReviewResponderEditSheet({
  open,
  onOpenChange,
  agentUid,
  agentName,
  setupData,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  agentUid: string
  agentName: string
  setupData: Record<string, unknown>
}) {
  const [fields, setFields] = useState<EditableFields>(() =>
    readFields(setupData),
  )
  const [wasOpen, setWasOpen] = useState(open)
  const [processing, setProcessing] = useState(false)

  // Reset to the server's view each time the drawer opens so a prior cancel
  // doesn't leave stale edits behind — adjusting state during render (the
  // React-recommended alternative to a setState effect).
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setFields(readFields(setupData))
  }

  function update<K extends keyof EditableFields>(
    key: K,
    value: EditableFields[K],
  ) {
    setFields((prev) => ({ ...prev, [key]: value }))
  }

  function save() {
    setProcessing(true)
    router.patch(
      setupAgentPath({ id: agentUid }),
      { setup_data: { ...fields } },
      {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="px-6 py-4">
          <SheetTitle>Edit {agentName}</SheetTitle>
        </SheetHeader>

        <Tabs
          defaultValue="tone"
          className="flex min-h-0 flex-1 flex-col gap-0"
        >
          <TabsBar>
            <TabsList variant="line">
              <TabsTrigger value="tone">Tone</TabsTrigger>
              <TabsTrigger value="rules">Rules</TabsTrigger>
              <TabsTrigger value="customization">Customization</TabsTrigger>
            </TabsList>
          </TabsBar>

          <TabsContent
            value="tone"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <TonePanel fields={fields} onChange={update} />
          </TabsContent>
          <TabsContent
            value="rules"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <RulesPanel fields={fields} onChange={update} />
          </TabsContent>
          <TabsContent
            value="customization"
            className="flex-1 space-y-8 overflow-y-auto px-6 py-5"
          >
            <CustomizationPanel fields={fields} onChange={update} />
          </TabsContent>
        </Tabs>

        <SheetFooter className="border-t px-6 py-4">
          <Button
            className="w-full rounded-sm"
            onClick={save}
            disabled={processing}
          >
            {processing ? "Saving…" : "Save changes"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

interface PanelProps {
  fields: EditableFields
  onChange: <K extends keyof EditableFields>(
    key: K,
    value: EditableFields[K],
  ) => void
}

function Section({
  title,
  description,
  descriptionClassName,
  children,
}: {
  title: string
  description?: string
  descriptionClassName?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold">{title}</p>
        {description && (
          <p
            className={cn(
              "text-muted-foreground mt-0.5 text-xs leading-relaxed",
              descriptionClassName,
            )}
          >
            {description}
          </p>
        )}
      </div>
      {children}
    </div>
  )
}

function ToggleRow({
  title,
  description,
  checked,
  onChange,
}: {
  title: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-muted-foreground mt-1 text-xs leading-relaxed">
          {description}
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  )
}

function TonePanel({ fields, onChange }: PanelProps) {
  return (
    <>
      <Section title="Response style">
        <div className="grid grid-cols-3 gap-2">
          {TONE_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              selected={fields.response_tone === opt.value}
              onClick={() => onChange("response_tone", opt.value)}
              title={opt.label}
              description={opt.description}
            />
          ))}
        </div>
      </Section>

      <Section
        title="Sign-off name"
        description="Appended to responses. Leave blank to omit."
      >
        <Input
          value={fields.sign_off_name}
          onChange={(e) => onChange("sign_off_name", e.target.value)}
          placeholder="e.g., The Team at Acme"
        />
      </Section>

      <div className="space-y-6">
        <ToggleRow
          title="Match review language"
          description="Reply in the same language the reviewer wrote in. When off, every reply is in English."
          checked={fields.match_language}
          onChange={(v) => onChange("match_language", v)}
        />
        <ToggleRow
          title="Disclose AI assistance"
          description="Add a brief note that responses are AI-assisted. Required in some regions."
          checked={fields.disclose_ai}
          onChange={(v) => onChange("disclose_ai", v)}
        />
      </div>
    </>
  )
}

function RulesPanel({ fields, onChange }: PanelProps) {
  return (
    <>
      <Section
        title="When a review includes criticism"
        description="Applies wherever criticism shows up, even inside an otherwise glowing review."
        descriptionClassName="text-sm"
      >
        <div className="grid grid-cols-2 gap-2">
          {CRITICISM_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              selected={fields.criticism_handling === opt.value}
              onClick={() => onChange("criticism_handling", opt.value)}
              title={opt.label}
              description={opt.description}
              descriptionClassName="text-sm"
            />
          ))}
        </div>
      </Section>

      <Section
        title="When a review includes praise"
        description="Applies wherever praise shows up, even inside an otherwise critical review."
        descriptionClassName="text-sm"
      >
        <div className="grid grid-cols-2 gap-2">
          {PRAISE_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              selected={fields.praise_handling === opt.value}
              onClick={() => onChange("praise_handling", opt.value)}
              title={opt.label}
              description={opt.description}
              descriptionClassName="text-sm"
            />
          ))}
        </div>
      </Section>
    </>
  )
}

function CustomizationPanel({ fields, onChange }: PanelProps) {
  return (
    <CustomInstructionsField
      value={fields.additional_instructions}
      placeholder={CUSTOM_INSTRUCTIONS_PLACEHOLDERS["review-responder"]}
      onChange={(value) => onChange("additional_instructions", value)}
    />
  )
}
